<template>

    <SiteAlert v-if="alert" :alert="alert"></SiteAlert>

    <header v-bind="$attrs" id="site-header" ref="rootElement" class="sitewide-header w-full z-40 left-0 top-0 p-4" :class="rootClasses">

        <div class="logo-backdrop absolute left-0 top-0 h-full" aria-hidden="true"></div>

        <div class="grid relative z-10">

            <div class="logo-container">
                <Link class="logo" :href="context.site.url">
                    <img :src="logoUrl" :alt="'Logo for ' + context.site.name">
                </Link>
            </div>

            <nav class="main-menu relative unscrolled hidden ht:flex justify-end items-center">
                <div v-for="link in context.nav.main_menu" class="top-level-link ml-6 relative">
                    <Link class="droplet-link text-white font-headline uppercase font-bold tracking-wider relative inline-block text-center whitespace-nowrap text-xs" :href="link.href">{{ link.name }}</Link>
                    <span class="accent absolute text-primary-400" aria-hidden="true">
                        <i class="fa-solid fa-droplet"></i>
                    </span>
                    <div class="children absolute left-0 bg-primary-800" v-if="link.children.length">
                        <div class="child-link" v-for="child in link.children">
                            <Link @click="linkOut" class="block p-2 bg-primary-800 transition-colors hover:bg-secondary-600 focus:bg-secondary-600 text-center" :href="child.href" v-html="child.name"></Link>
                        </div>
                    </div>
                </div>
            </nav>

            <div class="search-container flex items-center justify-end vt:justify-center">
                <button @click="openSearch" class="search-button inline-block appearance-none text-white relative">
                    <i class="fas fa-search relative z-10 text-xl"></i>
                    <span class="sr-only">Open Search Window</span>
                </button>
            </div>

            <div class="items-center hidden vt:flex">
                <SocialLinks :color="socialColor" :links="context.site.social_media"></SocialLinks>
            </div>

            <div v-if="context.nav.header_buttons.length" class="header-buttons flex items-center">
                <div v-for="button in context.nav.header_buttons">
                    <Button :label="button.name" :url="button.href" variant="button-3"></Button>
                </div>
            </div>

            <div class="burger-container px-2 flex ht:hidden items-center justify-end vt:justify-center">
                <button @click="openMobileMenu" class="burger-button inline-block appearance-none text-white relative">
                    <i class="fas fa-bars relative z-10 text-3xl"></i>
                    <span class="sr-only">Open Mobile Menu</span>
                </button>
            </div>

        </div>

    </header>

    <teleport to="body">
        <aside v-if="appState != 'idle'" :class="appState" class="search-popup bg-primary-900 bg-opacity-95 fixed z-50 left-0 top-0 w-full h-full flex items-center justify-center">
            <button @click="closeSearch" class="search-closer absolute text-white transition-colors hover:text-secondary-500 focus:text-secondary-500">
                <i class="fa fa-times text-4xl" aria-hidden="true"></i>
                <span class="sr-only">Close Search Window</span>
            </button>
            <SearchForm @searching="handleSearch"></SearchForm>
            <i class="giant-magnifying-glass fas fa-search text-white" aria-hidden="true"></i>
        </aside>
    </teleport>

</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import SearchForm from './SearchForm.vue';
import SocialLinks from './Utilities/SocialLinks.vue';
import SiteAlert from './Utilities/SiteAlert.vue';

const $ = window.jQuery;

export default {
    
    props: ['context', 'background'],

    setup(props) {

        const appState = ref('idle');
        const scrolled = ref(false);
        const rootElement = ref(null);
        const handleSearch = (e) => {
            appState.value = 'searching';
        };

        const linkingOut = ref(false);

        const myHeight = computed(() => {
            return rootElement.value.clientHeight;
        });

        function registerScrollListener() {
            $(window).scroll(function() {
                let distance = $(window).scrollTop();
                if (distance >= myHeight.value) {
                    scrolled.value = true;
                } else {
                    scrolled.value = false;
                }
            });
        }

        function setBottomMargin() {
            document.documentElement.style.setProperty('--header-margin', (myHeight.value * -1) + 'px');
        }

        onMounted(registerScrollListener);
        onMounted(setBottomMargin);

        const logoUrl = computed(() => {
            return (props.background == 'dark' || scrolled.value)
                ? props.context.site.theme_url + '/img/header-logo-white.png'
                : props.context.site.theme_url + '/img/header-logo-dark.png';
        });

        const socialColor = computed(() => {
            return (props.background == 'dark' || scrolled.value)
                ? 'text-white'
                : 'text-primary-700'
        });

        onMounted(() => {
            Inertia.on('start', (e) => {
                scrolled.value = false;
            });
        });

        const rootClasses = computed(() => {
            let output = scrolled.value
                ? 'scrolled'
                : 'unscrolled';
            output = output + ' ' + props.background + '-background';

            if (linkingOut.value) {
                output = output + ' linking-out';
            }

            return output;
        });

        const openSearch = async () => {
            appState.value = 'popping-in';
            await window.wait(3); // idk why this works
            appState.value = 'popped';
        };

        const openMobileMenu = () => {
            let e = new CustomEvent("openMobileMenu", {
                detail: {}
            });
            window.dispatchEvent(e);
        };

        const closeSearch = async () => {
            appState.value = 'popping-out';
            await window.wait(200);
            appState.value = 'idle';
        };

        const linkOut = () => {
            linkingOut.value = true;
        };

        return {
            alert: usePage().props.value.site.alert,
            appState,
            closeSearch,
            handleSearch,
            linkOut,
            logoUrl,
            openMobileMenu,
            openSearch,
            rootElement,
            rootClasses,
            scrolled,
            socialColor
        };

    },

    components: {
        SiteAlert,
        SearchForm,
        SocialLinks
    }

}
</script>